import { ClbButton, ClbTooltip } from '@celebration/react';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useRef, useState } from 'react';
import IPoolLoadCardProps from './interfaces/IPoolLoadCardProps';
import LoadCardHeader from '../../../../common/components/load-card/load-card-header/load-card-header';
import LoadCardBody from '../../../../common/components/load-card/load-card-body/load-card-body';
import LoadCardFooter from '../../../../common/components/load-card/load-card-footer/load-card-footer';
import './pool-load-card.scss';
import {
    getPoolLoadTagStatusType,
    getRouteDetails,
    hasRateForCarrier,
} from './utils/pool-load-card-utils';
import LanguageContext from '../../../../app/contexts/language';
import { formatCurrency } from '../../../../utils/currency-utils';
import { getCardCursor } from '../../../../common/components/load-card/load-card-body/utils/load-card-body-utils';
import LoadCardDetail from '../../../../common/components/load-card-detail';
import { clickCard } from '../../../../common/utils/load-card-utils';
import LoadStatus from '../../../../common/enums/loads/load-status.enum';
import { getUTCDate } from '../../../../utils/date-utils';

export default function LoadCard(props: Readonly<IPoolLoadCardProps>) {
    const { loadInfo, numberOfSelectedCards, onConfirm } = props;

    const { t } = useTranslation();
    const { language } = LanguageContext.useLanguage();

    const [isPoolLoadCardDetailsVisible, setIsPoolLoadCardDetailsVisible] =
        useState(false);

    const hasRate = useMemo(() => hasRateForCarrier(loadInfo.rate), [loadInfo]);

    const routeDetails = useMemo(
        () => getRouteDetails(loadInfo.routeDetails),
        [loadInfo],
    );

    const checkBoxRef = useRef<HTMLInputElement>(null);

    const handleCardClick = useCallback(
        (event: any) => {
            clickCard(checkBoxRef.current!, event, numberOfSelectedCards);
        },
        [numberOfSelectedCards],
    );

    const renderFooterButtons = () => {
        return (
            <ClbButton
                size="sm"
                styleType="primary"
                label={t('Confirmar')}
                disabled={
                    !hasRate || loadInfo.tagStatus === LoadStatus.Confirmed
                }
                data-testid="card-pool-loads-footer__buttons-confirm"
                onClick={onConfirm}
            />
        );
    };

    const renderFooterButtonIcons = () => {
        return (
            <ClbTooltip
                description={t('Info')}
                position="top"
                showArrow
                colorMode="dark"
            >
                <ClbButton
                    icon="info"
                    size="md"
                    styleType="icon"
                    data-testid="card-loads-footer__icons-info"
                    onClick={() => {
                        setIsPoolLoadCardDetailsVisible(true);
                    }}
                />
            </ClbTooltip>
        );
    };

    const renderPoolLoadDetails = () => {
        return (
            isPoolLoadCardDetailsVisible && (
                <div className="card-load-detail">
                    <LoadCardDetail
                        initialVisibility
                        onClose={() => setIsPoolLoadCardDetailsVisible(false)}
                        loadData={loadInfo}
                        isPool
                    />
                </div>
            )
        );
    };

    const loadConfirmedByUserCarrier = () =>
        loadInfo.confirmedBy !== loadInfo.rate?.rateCarrierCode;

    return (
        <div
            role="none"
            key={`${loadInfo.idLoad}`}
            data-testid="card-loads__card"
            className={`card-loads ${loadInfo.tagStatus === LoadStatus.Confirmed ? 'card-load__confirmed' : ''} ${loadConfirmedByUserCarrier() ? 'card-load__confirmed_by_other' : ''}`}
            onClick={handleCardClick}
            style={{ cursor: getCardCursor(numberOfSelectedCards) }}
        >
            <div className="card-loads-main">
                <LoadCardHeader
                    loadId={loadInfo.idLoad}
                    showLoadId
                    checkBoxRef={checkBoxRef}
                    checked={loadInfo.checked}
                    tag={getPoolLoadTagStatusType(
                        hasRate,
                        loadInfo.tagStatus,
                        loadConfirmedByUserCarrier(),
                    )}
                    checkboxDisabled
                />

                <LoadCardBody
                    sourceRoute={routeDetails.source}
                    destinationRoute={routeDetails.destination}
                    collectionDate={getUTCDate(
                        loadInfo.routeDetails.collectionDate,
                    )}
                    channel={loadInfo?.channel}
                    loadType={loadInfo.type}
                    weight={`${loadInfo.shipment.weight} ${loadInfo.shipment.weightUnitMeasure}`}
                    price={formatCurrency(
                        loadInfo.rate?.chargeAmount ?? 123456,
                        loadInfo.shipment.currency,
                        language,
                    )}
                    hasRate={hasRate}
                    skids={loadInfo.shipment.skids}
                    isReturnable={loadInfo.isReturnable}
                />

                <LoadCardFooter
                    mainButtons={renderFooterButtons()}
                    iconButtons={renderFooterButtonIcons()}
                    hiddenIconButtons={false}
                    isPool
                    hasRate={hasRate}
                />
            </div>

            {renderPoolLoadDetails()}
        </div>
    );
}
